import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import {
  useFleetApi,
  type AgentRow,
  type AutonomyMode,
  type AutonomySignalRow,
} from "./api";

function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function role(agent: string): "watcher" | "operator" | "specialist" {
  const a = agent.toLowerCase();
  if (a.includes("sentinel") || a.includes("watcher") || a.includes("drift") || a.includes("market") || a.includes("feed")) return "watcher";
  if (a.includes("detective") || a.includes("investigator")) return "specialist";
  return "operator";
}

const HDR: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--text-3)",
};

// Recommend promotion to auto_under_threshold when there's a track record of
// approvals and no recent (or no ever) revert. 5 approvals + 14-day clean
// history matches the threshold conversation we've been having; this is just
// a UI hint, the operator still clicks the button.
function recommendPromotion(sig: AutonomySignalRow): boolean {
  const currentMode = sig.mode ?? "escalate_every_write";
  if (currentMode !== "escalate_every_write") return false;
  if ((sig.approvals_count ?? 0) < 5) return false;
  if (sig.last_revert_at) {
    const days = (Date.now() - new Date(sig.last_revert_at).getTime()) / 86_400_000;
    if (days < 14) return false;
  }
  return true;
}

function modeLabel(mode: AutonomyMode | null | undefined): string {
  if (!mode) return "escalate";
  if (mode === "escalate_every_write") return "escalate";
  if (mode === "auto_under_threshold") return "auto (threshold)";
  if (mode === "auto") return "auto";
  if (mode === "disabled") return "disabled";
  return mode;
}

function modeTone(mode: AutonomyMode | null | undefined): string {
  if (!mode || mode === "escalate_every_write") return "muted";
  if (mode === "disabled") return "crit";
  return "ok";
}

interface AutonomyDetailProps {
  agent: string;
  signals: AutonomySignalRow[];
  onChanged: (updated: AutonomySignalRow) => void;
}

function AutonomyDetail({ agent, signals, onChanged }: AutonomyDetailProps) {
  const { token, logout, user } = useAuth();
  const { setAutonomy } = useFleetApi(token, logout);
  const actor = user?.username || "arthur";

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const flip = useCallback(
    async (sig: AutonomySignalRow, mode: AutonomyMode, reason?: string) => {
      const key = `${sig.situation_class}__${sig.decision_class}__${mode}`;
      setBusyKey(key);
      setErr(null);
      try {
        const updated = await setAutonomy(agent, {
          situation_class: sig.situation_class,
          decision_class: sig.decision_class,
          mode,
          reason: reason ?? null,
          actor,
        });
        onChanged(updated);
      } catch (e) {
        const raw = e instanceof Error ? e.message : String(e);
        // Mirror the Escalations.tsx pattern: try to parse the embedded JSON
        // body so a 400/422 detail surfaces cleanly instead of as a raw
        // "setAutonomy failed: 400 {...}".
        try {
          const start = raw.indexOf("{");
          if (start !== -1) {
            const parsed = JSON.parse(raw.slice(start));
            const detail = parsed?.detail ?? parsed;
            if (typeof detail === "string") {
              setErr(detail);
              return;
            }
            if (detail && typeof detail === "object") {
              setErr(JSON.stringify(detail));
              return;
            }
          }
        } catch {
          // fall through
        }
        setErr(raw);
      } finally {
        setBusyKey(null);
      }
    },
    [agent, actor, onChanged, setAutonomy],
  );

  if (signals.length === 0) {
    return (
      <div
        className="muted"
        style={{ gridColumn: "1 / -1", padding: "8px 4px 12px", fontSize: "12px" }}
      >
        No autonomy_signal recorded yet — the runtime hasn't written approvals
        for this agent. Pre-emptive disabling requires the agent to be in
        fleet.agent_state (it is if it appears here).
      </div>
    );
  }

  return (
    <div
      style={{
        gridColumn: "1 / -1",
        background: "var(--surface-2, rgba(0,0,0,0.02))",
        border: "1px solid var(--border)",
        borderRadius: "6px",
        padding: "10px 12px",
        marginBottom: "4px",
      }}
    >
      {err && (
        <div style={{ color: "var(--crit)", fontSize: "11.5px", marginBottom: "6px" }}>
          {err}
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.6fr auto auto auto auto 1fr",
          rowGap: "6px",
          columnGap: "14px",
          alignItems: "center",
        }}
      >
        <div style={HDR}>Situation &rarr; Decision</div>
        <div style={HDR}>Approvals</div>
        <div style={HDR}>Last approval</div>
        <div style={HDR}>Reverts?</div>
        <div style={HDR}>Mode</div>
        <div style={HDR}>Action</div>

        {signals.map(sig => {
          const recommend = recommendPromotion(sig);
          const currentMode = sig.mode ?? "escalate_every_write";
          const promoteKey = `${sig.situation_class}__${sig.decision_class}__auto_under_threshold`;
          const demoteKey = `${sig.situation_class}__${sig.decision_class}__escalate_every_write`;
          const disableKey = `${sig.situation_class}__${sig.decision_class}__disabled`;
          const anyBusy = busyKey !== null;
          return (
            <Fragment key={`${sig.situation_class}__${sig.decision_class}`}>
              <div style={{ fontSize: "12.5px" }}>
                <span style={{ color: "var(--text-2)" }}>
                  {sig.situation_class.replace(/_/g, " ")}
                </span>
                <span style={{ color: "var(--text-3)" }}> &rarr; </span>
                <span>{sig.decision_class.replace(/_/g, " ")}</span>
              </div>
              <div style={{ fontVariantNumeric: "tabular-nums", fontSize: "12.5px" }}>
                {sig.approvals_count}
              </div>
              <div
                style={{
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--text-2)",
                  fontSize: "12.5px",
                }}
              >
                {fmtAgo(sig.last_approval_at)}
              </div>
              <div
                style={{
                  fontSize: "12.5px",
                  color: sig.last_revert_at ? "var(--warn)" : "var(--text-2)",
                }}
              >
                {sig.last_revert_at ? fmtAgo(sig.last_revert_at) : "none"}
              </div>
              <div>
                <span className={`pill ${modeTone(currentMode)}`} style={{ fontSize: "11px" }}>
                  {modeLabel(currentMode)}
                </span>
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {(currentMode === "escalate_every_write" || currentMode === null || currentMode === "disabled") && (
                  <button
                    className={`opt${recommend ? " recommended" : ""}`}
                    disabled={anyBusy}
                    onClick={() => flip(sig, "auto_under_threshold")}
                    style={{ fontSize: "11.5px", padding: "4px 8px" }}
                    title={
                      recommend
                        ? "Recommended — 5+ approvals, no recent reverts"
                        : "Promote to auto under threshold"
                    }
                  >
                    {busyKey === promoteKey ? "..." : "Promote to auto_under_threshold"}
                  </button>
                )}
                {(currentMode === "auto_under_threshold" || currentMode === "auto") && (
                  <>
                    <button
                      className="opt"
                      disabled={anyBusy}
                      onClick={() => flip(sig, "escalate_every_write")}
                      style={{ fontSize: "11.5px", padding: "4px 8px" }}
                    >
                      {busyKey === demoteKey ? "..." : "Demote to escalate"}
                    </button>
                    <button
                      className="opt crit"
                      disabled={anyBusy}
                      onClick={() => flip(sig, "disabled")}
                      style={{ fontSize: "11.5px", padding: "4px 8px" }}
                    >
                      {busyKey === disableKey ? "..." : "Disable"}
                    </button>
                  </>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function Agents() {
  const { token, logout } = useAuth();
  const { fetchAgents } = useFleetApi(token, logout);
  const [rows, setRows] = useState<AgentRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    fetchAgents()
      .then(r => { if (alive) setRows(r); })
      .catch(e => { if (alive) setErr(String(e)); });
    return () => { alive = false; };
  }, [fetchAgents]);

  const toggleExpand = useCallback((agent: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(agent)) next.delete(agent);
      else next.add(agent);
      return next;
    });
  }, []);

  const applyAutonomyChange = useCallback(
    (agent: string, updated: AutonomySignalRow) => {
      setRows(prev => {
        if (!prev) return prev;
        return prev.map(r => {
          if (r.agent !== agent) return r;
          const existing = r.autonomy_signal ?? [];
          const idx = existing.findIndex(
            s => s.situation_class === updated.situation_class
              && s.decision_class === updated.decision_class,
          );
          const merged: AutonomySignalRow[] = idx >= 0
            ? existing.map((s, i) => i === idx ? { ...s, ...updated } : s)
            : [...existing, updated];
          merged.sort((a, b) => {
            const sa = a.situation_class.localeCompare(b.situation_class);
            return sa !== 0 ? sa : a.decision_class.localeCompare(b.decision_class);
          });
          return { ...r, autonomy_signal: merged };
        });
      });
    },
    [],
  );

  const countLabel = useMemo(() => {
    if (rows == null) return "loading...";
    return `${rows.length} agent${rows.length === 1 ? "" : "s"} seen`;
  }, [rows]);

  return (
    <section>
      <p className="page-lede">
        {countLabel}. Every agent the runtime has emitted activity for. Health is derived live from{" "}
        <code>fleet.audit</code>, <code>fleet.task</code> and <code>fleet.agent_state</code>.
        Click a row to expand its autonomy signal and flip per-pair modes.
      </p>

      {err && <p style={{ color: "var(--crit)", fontSize: "12.5px" }}>{err}</p>}

      {rows && rows.length === 0 && (
        <p className="muted">No agents have emitted activity yet.</p>
      )}

      {rows && rows.length > 0 && (
        <div
          className="card"
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr auto auto auto auto auto",
            rowGap: "8px",
            columnGap: "16px",
            alignItems: "center",
          }}
        >
          <div style={HDR}>Agent</div>
          <div style={HDR}>Last seen</div>
          <div style={HDR}>Events (24h)</div>
          <div style={HDR}>Tasks 24h</div>
          <div style={HDR}>Open esc.</div>
          <div style={HDR}>State</div>

          {rows.map(a => {
            const tasksFailed = a.tasks_failed_24h ?? 0;
            const tasksResolved = a.tasks_resolved_24h ?? 0;
            const tasksSummary =
              tasksFailed + tasksResolved === 0
                ? "—"
                : `${tasksResolved} ok / ${tasksFailed} fail`;
            const stateTone =
              a.enabled === false || (a.consecutive_errors ?? 0) > 2
                ? "crit"
                : tasksFailed > 0
                  ? "warn"
                  : "ok";
            const stateLabel =
              a.enabled === false
                ? "disabled"
                : (a.consecutive_errors ?? 0) > 0
                  ? `${a.consecutive_errors} cons. err`
                  : "healthy";
            const isOpen = expanded.has(a.agent);
            // Disable expansion for rows that have no agent_state row at all —
            // there's nothing to flip and we'd 404 on every button press.
            const hasState = a.enabled !== null && a.enabled !== undefined;
            return (
              <Fragment key={a.agent}>
                <div
                  style={{
                    cursor: hasState ? "pointer" : "default",
                    userSelect: "none",
                  }}
                  onClick={hasState ? () => toggleExpand(a.agent) : undefined}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "14px",
                      color: "var(--text-3)",
                      fontSize: "10px",
                    }}
                  >
                    {hasState ? (isOpen ? "▾" : "▸") : ""}
                  </span>
                  <Link
                    to={`/fleet/activity?agent=${encodeURIComponent(a.agent)}`}
                    onClick={e => e.stopPropagation()}
                  >
                    {a.agent.replace(/_/g, " ")}
                  </Link>
                  <span
                    className={`pill ${role(a.agent)}`}
                    style={{ marginLeft: "8px", fontSize: "10.5px" }}
                  >
                    {role(a.agent)}
                  </span>
                </div>
                <div
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    color: "var(--text-2)",
                    fontSize: "12.5px",
                  }}
                >
                  {fmtAgo(a.last_seen)}
                </div>
                <div style={{ fontVariantNumeric: "tabular-nums" }}>{a.audit_24h}</div>
                <div
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    color: tasksFailed > 0 ? "var(--crit)" : "var(--text-2)",
                    fontSize: "12.5px",
                  }}
                >
                  {tasksSummary}
                </div>
                <div
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    color: a.open_escalations > 0 ? "var(--warn)" : "var(--text-2)",
                  }}
                >
                  {a.open_escalations}
                </div>
                <div>
                  <span className={`pill ${stateTone}`}>{stateLabel}</span>
                </div>
                {isOpen && hasState && (
                  <AutonomyDetail
                    agent={a.agent}
                    signals={a.autonomy_signal ?? []}
                    onChanged={updated => applyAutonomyChange(a.agent, updated)}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      )}
    </section>
  );
}
